import i18next from 'i18next';
import i18nextBrowserLanguagedetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
//Import all translation files
import enTranslations from '@/public/locales/en.json';
import itTranslations from '@/public/locales/it.json';

// Definisce il path base dell'applicazione
export const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH
  ? `/${process.env.NEXT_PUBLIC_BASE_PATH}`
  : '';

// Tipo per gestire le chiavi annidate delle traduzioni
type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

/** Lista di tutte le chiavi di traduzioni disponibili */
export type TranslationKeys = NestedKeyOf<typeof enTranslations> | string;

// Prepariamo tutte le traduzioni locali
const localResources = {
  en: { translation: enTranslations },
  it: { translation: itTranslations },
};

// Funzione per caricare le traduzioni remote e fare il merge con quelle locali
const loadRemoteTranslations = (language: string) => {
  const remoteUrl = `https://lcert-lokalise-pr-bucket.s3.eu-west-1.amazonaws.com/securdrive/${language}.json`;

  fetch(remoteUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((remoteTranslations) => {
      // Merge delle traduzioni remote con quelle locali
      i18next.addResourceBundle(
        language,
        'translation',
        remoteTranslations,
        true, // deep merge
        true, // overwrite
      );
      console.log(`Remote translations loaded and merged for: ${language}`);
    })
    .catch((error) => {
      console.error(
        `Failed to load remote translations for ${language}:`,
        error,
      );
    });
};

// Inizializzazione di i18next
i18next
  // Plugin per rilevare automaticamente la lingua del browser
  .use(i18nextBrowserLanguagedetector)
  // Plugin per React
  .use(initReactI18next)
  .init(
    {
      // Configurazione del rilevamento della lingua
      detection: {
        // Ordine di ricerca della lingua: query string -> cookie -> localStorage -> browser
        order: ['querystring', 'cookie', 'localStorage', 'navigator'],
        // Nome del parametro nella query string
        lookupQuerystring: 'lng',
        // Nome del cookie per la lingua
        lookupCookie: 'i18next',
        // Chiave nel localStorage per la lingua
        lookupLocalStorage: 'i18nextLng',
        // Dove salvare la lingua rilevata
        caches: ['localStorage', 'cookie'],
      },
      // Lingue supportate dall'applicazione
      supportedLngs: ['en', 'it'],
      // Precarica le traduzioni locali
      resources: localResources,
      // Namespace di default per le traduzioni
      defaultNS: 'translation',
      // Lingue di fallback in ordine di priorità
      fallbackLng: ['en', 'it'],
      // Configurazione per React
      react: {
        useSuspense: true,
      },
      // Funzione di interpolazione personalizzata per gestire i valori mancanti
      interpolation: {
        escapeValue: false,
      },
      // Debug mode per vedere i log dettagliati
      debug: process.env.NODE_ENV === 'development',
    },
    (err, t) => {
      if (err) {
        console.error('i18next initialization error:', err);
        return;
      }

      console.log('i18next initialized with local translations');

      // Carica le traduzioni remote per la lingua corrente
      const currentLanguage = i18next.language;
      console.log(`Current language detected: ${currentLanguage}`);
      loadRemoteTranslations(currentLanguage);

      // Aggiungi listener per il cambio lingua
      i18next.on('languageChanged', (newLanguage) => {
        console.log(`Language changed to: ${newLanguage}`);
        loadRemoteTranslations(newLanguage);
      });
    },
  );

export default i18next;
